import { IGoal } from "../models/goalModels";

/** Saved position first; the id breaks a tie, so two equal sortOrders never swap between renders. */
const bySavedOrder = (a: IGoal, b: IGoal) => a.sortOrder - b.sortOrder || a.id - b.id;

/**
 * The order the tree is shown in: open goals first, then the completed ones, each part by
 * its saved order. Sub-goals keep their saved order whether done or not, so a list reads the
 * same while it is being worked through. Returns a new tree; the input is left alone.
 */
export const sortGoalsForDisplay = (goals: IGoal[]): IGoal[] => {
  const withSortedSubGoals = goals.map((goal) => ({
    ...goal,
    subGoals: [...goal.subGoals].sort(bySavedOrder),
  }));

  return [
    ...withSortedSubGoals.filter((goal) => !goal.isCompleted).sort(bySavedOrder),
    ...withSortedSubGoals.filter((goal) => goal.isCompleted).sort(bySavedOrder),
  ];
};

/** The row with the new state, or the same row when nothing changes (its completedOn stays). */
const withCompletion = (goal: IGoal, isCompleted: boolean, now: string): IGoal =>
  goal.isCompleted === isCompleted
    ? goal
    : { ...goal, isCompleted, completedOn: isCompleted ? now : null };

/**
 * The completion rules the API applies, for the optimistic update: a goal with sub-goals
 * takes all of them along; a sub-goal's parent is completed exactly when all its sub-goals
 * are; a goal without sub-goals simply follows its checkbox. Only the rows that change get
 * a new completedOn. Returns the same array when the id is not in the tree.
 */
export const applyCompletion = (goals: IGoal[], id: number, isCompleted: boolean): IGoal[] => {
  const now = new Date().toISOString();

  if (goals.some((goal) => goal.id === id)) {
    return goals.map((goal) =>
      goal.id === id
        ? withCompletion(
            {
              ...goal,
              subGoals: goal.subGoals.map((subGoal) => withCompletion(subGoal, isCompleted, now)),
            },
            isCompleted,
            now,
          )
        : goal,
    );
  }

  const parent = goals.find((goal) => goal.subGoals.some((subGoal) => subGoal.id === id));
  if (!parent) {
    return goals;
  }

  return goals.map((goal) => {
    if (goal.id !== parent.id) {
      return goal;
    }

    const subGoals = goal.subGoals.map((subGoal) =>
      subGoal.id === id ? withCompletion(subGoal, isCompleted, now) : subGoal,
    );

    return withCompletion(
      { ...goal, subGoals },
      subGoals.every((subGoal) => subGoal.isCompleted),
      now,
    );
  });
};
